/**
 * Deferred monitoring initialization.
 *
 * PostHog, Sentry, and Hotjar are loaded asynchronously after the initial
 * render to reduce the critical JS bundle size by ~258KB.
 *
 * PostHog is initialized first (Sentry integration references it), then
 * Sentry, then Hotjar.
 */

let initialized = false;

export async function initMonitoring(): Promise<void> {
  if (initialized) return;
  initialized = true;

  // --- PostHog ---
  // TODO(nicsmith): no PostHog project for this platform yet. The key and
  // ui_host that shipped with the DMY clone were hardcoded here and initialized
  // unconditionally, which pointed this fork's pageviews, session recordings and
  // identified users at DMY's analytics instance. Gated on env until there is a
  // project of our own to send to.
  const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
  const { default: posthog } = await import('posthog-js');
  if (posthogKey) {
    posthog.init(posthogKey, {
      api_host: '/ingest',
      ui_host: import.meta.env.VITE_POSTHOG_UI_HOST,
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
      capture_exceptions: true,
      enable_recording_console_log: true,
      session_recording: {
        // Must stay false: the platform embeds cross-origin iframes (YouTube, Stripe)
        // that don't run PostHog, so cross-origin recording only throws
        // "Permission denied to access property __POSTHOG_INSTRUMENTED__" SecurityErrors.
        recordCrossOriginIframes: false,
        inlineStylesheet: true,
        collectFonts: true,
      },
      persistence: 'localStorage',
      defaults: '2026-01-30',
      loaded: (ph) => {
        if (!ph.sessionRecordingStarted) {
          ph.startSessionRecording();
        }
      },
    });
  }

  // --- Sentry ---
  if (import.meta.env.VITE_SENTRY_DSN) {
    const Sentry = await import('@sentry/react');
    const { isChunkError } = await import('./chunk-error-recovery');
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.MODE,
      release: import.meta.env.VITE_APP_VERSION,
      enabled: import.meta.env.PROD,
      tracesSampleRate: 0.1,
      // TODO(nicsmith): the PostHog<->Sentry cross-link named DMY's Sentry org
      // and project. Restore it once this platform has its own, together with
      // the sentryUrl built in beforeSend below.
      integrations: [],
      beforeSend(event) {
        // Drop browser-extension noise (MetaMask, Phantom, Coinbase, Rabby,
        // ad-blockers, password managers, etc.). These extensions inject
        // content scripts into every page and their internal errors bubble
        // up to window.onerror — Sentry catches them as if they were ours.
        // Has nothing to do with our code.
        try {
          const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
          const fromExtension = frames.some((f) => {
            const url = f.filename || f.abs_path || '';
            return (
              url.startsWith('chrome-extension://') ||
              url.startsWith('moz-extension://') ||
              url.startsWith('safari-extension://') ||
              url.startsWith('safari-web-extension://')
            );
          });
          if (fromExtension) return null;
        } catch (_) {
          /* never let the filter throw */
        }

        // Drop the classic ResizeObserver non-error that every browser SDK eats.
        const filterMsg =
          event.exception?.values?.[0]?.value || event.message || '';
        if (
          filterMsg.includes('ResizeObserver loop limit exceeded') ||
          filterMsg.includes('ResizeObserver loop completed with undelivered notifications')
        ) {
          return null;
        }

        // Drop "Non-Error promise rejection captured" with empty/undefined
        // reasons — these almost always come from third-party scripts that
        // throw non-Error values (analytics/ads/etc.).
        if (
          filterMsg === 'Non-Error promise rejection captured with value: undefined' ||
          filterMsg === 'Non-Error promise rejection captured with value: null'
        ) {
          return null;
        }

        // Drop stale-chunk errors after a deploy — chunk-error-recovery
        // already auto-reloads these tabs, so they're not actionable bugs.
        if (isChunkError(filterMsg)) {
          return null;
        }

        // Drop PostHog cross-origin instrumentation SecurityErrors (analytics
        // touching cross-origin iframes like YouTube/Stripe). Not our bug.
        if (
          filterMsg.includes('__POSTHOG_INSTRUMENTED__') ||
          (filterMsg.includes('Permission denied to access property') && filterMsg.includes('POSTHOG'))
        ) {
          return null;
        }

        // TODO(nicsmith): mirrored errors into PostHog with a deep link into
        // DMY's Sentry org. Both the org and the PostHog project were DMY's, so
        // this is off until this platform has its own pair.
        if (event.event_id && (event.level === 'error' || event.level === 'fatal')) {
          posthog.capture('sentry_exception', {
            exception_message: event.message || event.exception?.values?.[0]?.value || 'Unknown error',
            exception_type: event.exception?.values?.[0]?.type || 'Error',
            sentry_event_id: event.event_id,
            level: event.level,
          });
        }
        return event;
      },
    });
  }

  // --- Hotjar ---
  // TODO(nicsmith): a site ID belonging to another property was hardcoded here
  // and initialized unconditionally. Set VITE_HOTJAR_ID for our own.
  const hotjarId = Number(import.meta.env.VITE_HOTJAR_ID);
  if (hotjarId) {
    const { default: Hotjar } = await import('@hotjar/browser');
    Hotjar.init(hotjarId, 6);
  }
}
